import React from "react";
import styled from "styled-components";
import {
  compose,
  flattenProp,
  withProps,
  branch,
  renderComponent
} from "recompose";
import { Tag, Intent, Icon, Tooltip } from "@blueprintjs/core";
import { distanceInWordsToNow, parse } from "date-fns";
import en from "date-fns/locale/en";
import it from "date-fns/locale/it";
import de from "date-fns/locale/de";
import UserInfo from "app/auth/UserInfo";
import { withRatings } from "app/ratings/hocs";
import CommentCount from "app/comments/CommentCount";
import { withCommentCount } from "app/comments/hocs";
import CountIconStyle from "app/CountIconStyle";
import { FormattedMessage, injectIntl } from "react-intl";
import { dateLocale } from "app/utils";

const Muted = styled.div.attrs({
  className: "pt-text-muted"
})`
  font-size: 0.857em;
`;

const Users = props => {
  const { users } = props;
  return (
    <span>
      {users.map((userId, index) => (
        <span key={index}>
          {index > 0 && ", "}
          <UserInfo userId={userId} key={userId} />
        </span>
      ))}{" "}
      liked this
    </span>
  );
};

const Likes = withRatings(props => {
  const { ratings } = props;
  if (!ratings) return null;
  const result = ratings.result;
  const loading = !result;
  const count = loading ? "⌛" : result.length;
  const users = result ? result.map(rating => rating.userId) : [];
  return (
    <Tooltip content={<Users users={users} />} isDisabled={users.length === 0}>
      <CountIconStyle loading={loading} muted={loading || !(users.length > 0)}>
        <Icon iconName="thumbs-up" className="count-icon" title="Likes" />
        <span className="count">{count}</span>
      </CountIconStyle>
    </Tooltip>
  );
});

const Comments = compose(
  withProps(props => {
    const { proposalId: resourceId, isDraft } = props;
    const resourceType = isDraft ? "Draft" : "Proposal";
    return { resourceId, resourceType };
  }),
  withCommentCount
)(CommentCount);

const Byline = props => {
  const {
    userId,
    created,
    modified,
    isDraft,
    displayModifed,
    displayCreated,
    proposal,
    intl
  } = props;
  return (
    <Muted>
      {!isDraft &&
        intl.formatMessage(
          {
            id: "proposal.byline.date.published",
            defaultMessage: "Published {date}"
          },
          { date: displayModifed }
        )}
      {isDraft &&
        (modified > created
          ? intl.formatMessage(
              {
                id: "proposal.byline.date.modified",
                defaultMessage: "Modified {date}"
              },
              { date: displayModifed }
            )
          : intl.formatMessage(
              {
                id: "proposal.byline.date.created",
                defaultMessage: "Created {date}"
              },
              { date: displayCreated }
            ))}{" "}
      <FormattedMessage
        id="proposal.byline.date.ago"
        defaultMessage="ago by {user}"
        values={{
          user: <UserInfo userId={userId} />
        }}
      />{" "}
      {isDraft ? (
        <Tag intent={Intent.WARNING} className="pt-minimal">
          <FormattedMessage id="proposal.status.draft" defaultMessage="Draft" />
        </Tag>
      ) : (
        <Tag intent={Intent.SUCCESS} className="pt-minimal">
          <FormattedMessage
            id="proposal.status.published"
            defaultMessage="Published"
          />
        </Tag>
      )}{" "}
      {!isDraft && <Likes proposalId={proposal._id} />}
      <Comments proposalId={proposal._id} isDraft={isDraft} />
    </Muted>
  );
};

const SampleDataByline = props => {
  const { proposal, isDraft } = props;
  return (
    <Muted>
      <Tag intent={Intent.PRIMARY} className="pt-minimal">
        Sample data
      </Tag>{" "}
      {props.isDraft ? (
        <Tag intent={Intent.WARNING} className="pt-minimal">
          Draft
        </Tag>
      ) : (
        <Tag intent={Intent.SUCCESS} className="pt-minimal">
          <FormattedMessage
            id="proposal.status.published"
            defaultMessage="Published"
          />
        </Tag>
      )}{" "}
      {!isDraft && <Likes proposalId={proposal._id} />}
      <Comments proposalId={proposal._id} isDraft={isDraft} />
    </Muted>
  );
};

const normalizeProps = props => {
  const created = parse(props.created);
  const modified = parse(props.modified);
  const displayCreated = distanceInWordsToNow(created, {
    locale: dateLocale[props.intl.locale]
  });
  const displayModifed = distanceInWordsToNow(modified, {
    locale: dateLocale[props.intl.locale]
  });
  return { displayModifed, displayCreated, created, modified };
};

const enhance = compose(
  flattenProp("proposal"),
  flattenProp("ui"),
  injectIntl,
  withProps(normalizeProps),
  branch(props => props.isSampleData, renderComponent(SampleDataByline))
);

export default enhance(Byline);



// WEBPACK FOOTER //
// ./src/app/proposals/Byline.js